import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../css/ExportModal.css';

const ExportModal = ({ board, onClose }) => {
    const { apiCall, getToken } = useAuth();

    // Selection mode: 'all', 'columns', 'labels', 'tasks'
    const [selectionMode, setSelectionMode] = useState('all');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);

    // Custom instructions
    const [instructions, setInstructions] = useState('');

    // Task search
    const [taskSearch, setTaskSearch] = useState('');

    // Documentation picker
    const [includeBoardContext, setIncludeBoardContext] = useState(true);
    const [docs, setDocs] = useState({ spaces: [], pages: [] });
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsLoaded, setDocsLoaded] = useState(false);
    const [selectedDocs, setSelectedDocs] = useState([]); // [{id, type}]
    const [docSearch, setDocSearch] = useState('');
    const [docPickerOpen, setDocPickerOpen] = useState(false);

    // Export format
    const [exportFormat, setExportFormat] = useState('markdown'); // 'markdown' or 'json'

    // Preview / export
    const [preview, setPreview] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [taskCount, setTaskCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('configure'); // 'configure' or 'preview'

    // Gather all labels across the board
    const allLabels = [...new Set(
        board.columns.flatMap(col => col.tasks.flatMap(t => t.labels || []))
    )].sort();

    // Gather all tasks (non-archived)
    const allTasks = board.columns.flatMap(col =>
        col.tasks.filter(t => !t.is_archived).map(t => ({
            ...t,
            columnName: col.name,
            boardTaskId: board.abbreviation
                ? `${board.abbreviation}-${t.board_number}`
                : `#${t.board_number || t.id}`
        }))
    );

    const filteredTasks = allTasks.filter(task => {
        const q = taskSearch.toLowerCase();
        return !q
            || task.boardTaskId.toLowerCase().includes(q)
            || task.title.toLowerCase().includes(q)
            || task.columnName.toLowerCase().includes(q);
    });

    // Load documentation — workspace docs if board is linked, personal docs otherwise
    const fetchDocs = useCallback(async () => {
        if (docsLoaded) return;
        setDocsLoading(true);
        try {
            let spaces = [];
            let pages = [];

            if (board.linked_workspace) {
                // Fetch workspace documentation
                // Workspace endpoints require auth-token header with Bearer prefix
                const wsId = board.linked_workspace.workspace_id;
                const idToken = await getToken();
                const wsHeaders = { headers: { 'auth-token': `Bearer ${idToken}` } };
                const [spacesRes, pagesRes] = await Promise.all([
                    apiCall(`/workspaces/${wsId}/spaces/`, wsHeaders),
                    apiCall(`/workspaces/${wsId}/pages/`, wsHeaders),
                ]);

                // Workspace endpoints return bare arrays
                const spacesData = spacesRes.ok ? await spacesRes.json() : [];
                const pagesData = pagesRes.ok ? await pagesRes.json() : [];
                spaces = Array.isArray(spacesData) ? spacesData : (spacesData.spaces || []);
                pages = Array.isArray(pagesData) ? pagesData : (pagesData.pages || []);
            } else {
                // Fetch personal documentation
                const [spacesRes, pagesRes] = await Promise.all([
                    apiCall(`/spaces/author/${encodeURIComponent(board.author)}/`),
                    apiCall(`/pages/author/${encodeURIComponent(board.author)}/`),
                ]);

                // Personal endpoints wrap data: {spaces: [...]} and {pages: [...]}
                if (spacesRes.ok) {
                    const spacesData = await spacesRes.json();
                    spaces = spacesData.spaces || [];
                }
                if (pagesRes.ok) {
                    const pagesData = await pagesRes.json();
                    pages = pagesData.pages || [];
                }
            }

            setDocs({
                spaces: Array.isArray(spaces) ? spaces : [],
                pages: Array.isArray(pages) ? pages : [],
            });
            setDocsLoaded(true);
        } catch (err) {
            console.error('Error fetching docs:', err);
        }
        setDocsLoading(false);
    }, [apiCall, getToken, board.author, board.linked_workspace, docsLoaded]);

    // Open doc picker triggers loading
    useEffect(() => {
        if (docPickerOpen && !docsLoaded) {
            fetchDocs();
        }
    }, [docPickerOpen, docsLoaded, fetchDocs]);

    // Generate preview
    const handleGeneratePreview = async () => {
        setPreviewLoading(true);
        setPreviewError('');

        const body = {
            format: exportFormat,
            custom_instructions: instructions,
            include_board_context: includeBoardContext,
            space_ids: selectedDocs.filter(d => d.type === 'space').map(d => d.id),
            page_ids: selectedDocs.filter(d => d.type === 'page').map(d => d.id),
        };

        if (selectionMode === 'columns' && selectedColumns.length > 0) {
            body.column_ids = selectedColumns;
        } else if (selectionMode === 'labels' && selectedLabels.length > 0) {
            body.labels = selectedLabels;
        } else if (selectionMode === 'tasks' && selectedTaskIds.length > 0) {
            body.task_ids = selectedTaskIds;
        }
        // 'all' mode sends no filters

        try {
            const response = await apiCall(`/tasks/boards/${board.id}/export/`, {
                method: 'POST',
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const data = await response.json();
                setPreview(exportFormat === 'json' ? data.json : data.markdown);
                setTaskCount(data.task_count);
                setCharCount(data.char_count);
                setActiveTab('preview');
            } else {
                const data = await response.json();
                setPreviewError(data.error || 'Failed to generate export');
            }
        } catch (err) {
            setPreviewError('Network error');
        }
        setPreviewLoading(false);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(preview);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = preview;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleDownload = () => {
        const isJson = exportFormat === 'json';
        const blob = new Blob([preview], { type: isJson ? 'application/json' : 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const boardSlug = board.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        a.download = `${boardSlug}-tasks-export.${isJson ? 'json' : 'md'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Toggle helpers
    const toggleColumn = (colId) => {
        setSelectedColumns(prev =>
            prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
        );
    };

    const toggleLabel = (label) => {
        setSelectedLabels(prev =>
            prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
        );
    };

    const toggleTask = (taskId) => {
        setSelectedTaskIds(prev =>
            prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
        );
    };

    const toggleDoc = (docId, docType) => {
        setSelectedDocs(prev => {
            const exists = prev.some(d => d.id === docId && d.type === docType);
            return exists
                ? prev.filter(d => !(d.id === docId && d.type === docType))
                : [...prev, { id: docId, type: docType }];
        });
    };

    const isDocSelected = (docId, docType) =>
        selectedDocs.some(d => d.id === docId && d.type === docType);

    // Merge spaces and pages into a single flat list for the picker
    const allDocs = [
        ...docs.spaces.map(s => ({ id: s.id, name: s.name, type: 'space' })),
        ...docs.pages.map(p => ({ id: p.id, name: p.page_name, type: 'page' })),
    ];
    const filteredDocs = allDocs.filter(d =>
        d.name.toLowerCase().includes(docSearch.toLowerCase())
    );

    // Validation
    const canGenerate = selectionMode === 'all'
        || (selectionMode === 'columns' && selectedColumns.length > 0)
        || (selectionMode === 'labels' && selectedLabels.length > 0)
        || (selectionMode === 'tasks' && selectedTaskIds.length > 0);

    return (
        <div className="ExportModal__overlay" onClick={onClose}>
            <div className="ExportModal" onClick={(e) => e.stopPropagation()}>
                <div className="ExportModal__header">
                    <div className="ExportModal__header-left">
                        <i className="fas fa-file-export" />
                        <h3 className="ExportModal__title">Export for AI</h3>
                    </div>
                    <button className="ExportModal__close" onClick={onClose}>&times;</button>
                </div>

                {/* Tabs */}
                <div className="ExportModal__tabs">
                    <button
                        className={`ExportModal__tab ${activeTab === 'configure' ? 'ExportModal__tab--active' : ''}`}
                        onClick={() => setActiveTab('configure')}
                    >
                        <i className="fas fa-sliders-h" /> Configure
                    </button>
                    <button
                        className={`ExportModal__tab ${activeTab === 'preview' ? 'ExportModal__tab--active' : ''}`}
                        onClick={() => activeTab === 'preview' || preview ? setActiveTab('preview') : null}
                        disabled={!preview}
                    >
                        <i className="fas fa-eye" /> Preview
                    </button>
                </div>

                {activeTab === 'configure' ? (
                    <div className="ExportModal__body">
                        {/* Task Selection */}
                        <div className="ExportModal__section">
                            <label className="ExportModal__label">Select Tasks</label>
                            <div className="ExportModal__mode-options">
                                {[
                                    { value: 'all', label: 'All Tasks', icon: 'fas fa-layer-group' },
                                    { value: 'columns', label: 'By Column', icon: 'fas fa-columns' },
                                    { value: 'labels', label: 'By Label', icon: 'fas fa-tags' },
                                    { value: 'tasks', label: 'Specific Tasks', icon: 'fas fa-tasks' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`ExportModal__mode-btn ${selectionMode === opt.value ? 'ExportModal__mode-btn--active' : ''}`}
                                        onClick={() => setSelectionMode(opt.value)}
                                    >
                                        <i className={opt.icon} /> {opt.label}
                                    </button>
                                ))}
                            </div>

                            {/* Column selection */}
                            {selectionMode === 'columns' && (
                                <div className="ExportModal__selection-list">
                                    {board.columns.map(col => (
                                        <label key={col.id} className="ExportModal__checkbox-item">
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(col.id)}
                                                onChange={() => toggleColumn(col.id)}
                                            />
                                            {col.color && (
                                                <span className="ExportModal__color-dot" style={{ backgroundColor: col.color }} />
                                            )}
                                            <span>{col.name}</span>
                                            <span className="ExportModal__item-count">
                                                {col.tasks.filter(t => !t.is_archived).length}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {/* Label selection */}
                            {selectionMode === 'labels' && (
                                <div className="ExportModal__selection-list">
                                    {allLabels.length === 0 ? (
                                        <div className="ExportModal__empty-hint">No labels on this board</div>
                                    ) : (
                                        allLabels.map(label => (
                                            <label key={label} className="ExportModal__checkbox-item">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLabels.includes(label)}
                                                    onChange={() => toggleLabel(label)}
                                                />
                                                <span className="ExportModal__label-chip">{label}</span>
                                            </label>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Task selection */}
                            {selectionMode === 'tasks' && (
                                <>
                                    <input
                                        className="ExportModal__task-search"
                                        type="text"
                                        value={taskSearch}
                                        onChange={(e) => setTaskSearch(e.target.value)}
                                        placeholder="Search by task ID, title, or column…"
                                        autoFocus
                                    />
                                    <div className="ExportModal__selection-list ExportModal__selection-list--tasks">
                                        {filteredTasks.length > 0 ? (
                                            filteredTasks.map(task => (
                                                <label key={task.id} className="ExportModal__checkbox-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTaskIds.includes(task.id)}
                                                        onChange={() => toggleTask(task.id)}
                                                    />
                                                    <span className="ExportModal__task-id-badge">{task.boardTaskId}</span>
                                                    <span className="ExportModal__task-title">{task.title}</span>
                                                    <span className="ExportModal__task-col-badge">{task.columnName}</span>
                                                </label>
                                            ))
                                        ) : (
                                            <div className="ExportModal__empty-hint">
                                                {taskSearch ? 'No tasks match your search' : 'No tasks on this board'}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Export Format */}
                        <div className="ExportModal__section">
                            <label className="ExportModal__label">Export Format</label>
                            <div className="ExportModal__mode-options">
                                {[
                                    { value: 'markdown', label: 'Markdown', icon: 'fas fa-file-alt' },
                                    { value: 'json', label: 'JSON', icon: 'fas fa-code' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`ExportModal__mode-btn ${exportFormat === opt.value ? 'ExportModal__mode-btn--active' : ''}`}
                                        onClick={() => setExportFormat(opt.value)}
                                    >
                                        <i className={opt.icon} /> {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Custom Instructions */}
                        <div className="ExportModal__section">
                            <label className="ExportModal__label">
                                Custom Instructions <span className="ExportModal__hint">(optional)</span>
                            </label>
                            <textarea
                                className="ExportModal__textarea"
                                value={instructions}
                                onChange={(e) => setInstructions(e.target.value)}
                                placeholder="e.g., Implement these tasks in the Django backend using function-based views. Use the existing project patterns..."
                                rows={3}
                            />
                        </div>

                        {/* Documentation Picker */}
                        <div className="ExportModal__section">
                            <label className="ExportModal__label">
                                Attach Documentation <span className="ExportModal__hint">(optional)</span>
                            </label>
                            <button
                                className={`ExportModal__doc-toggle ${docPickerOpen ? 'ExportModal__doc-toggle--open' : ''}`}
                                onClick={() => setDocPickerOpen(!docPickerOpen)}
                            >
                                <i className={`fas ${docPickerOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                {selectedDocs.length > 0
                                    ? `${selectedDocs.length} document${selectedDocs.length > 1 ? 's' : ''} selected`
                                    : 'Browse your documentation'
                                }
                            </button>

                            {docPickerOpen && (
                                <div className="ExportModal__doc-picker">
                                    <input
                                        className="ExportModal__doc-search"
                                        type="text"
                                        value={docSearch}
                                        onChange={(e) => setDocSearch(e.target.value)}
                                        placeholder="Search documents..."
                                    />

                                    {docsLoading ? (
                                        <div className="ExportModal__doc-loading">
                                            <span className="btn-spinner btn-spinner--dark" /> Loading...
                                        </div>
                                    ) : (
                                        <div className="ExportModal__doc-list">
                                            {filteredDocs.length > 0 ? (
                                                filteredDocs.map(doc => (
                                                    <label key={`${doc.type}-${doc.id}`} className="ExportModal__checkbox-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={isDocSelected(doc.id, doc.type)}
                                                            onChange={() => toggleDoc(doc.id, doc.type)}
                                                        />
                                                        <i className={`fas ${doc.type === 'space' ? 'fa-book' : 'fa-file-alt'} ExportModal__doc-icon`} />
                                                        <span>{doc.name}</span>
                                                    </label>
                                                ))
                                            ) : (
                                                <div className="ExportModal__empty-hint">
                                                    {docSearch ? 'No documents match your search' : 'No documents found'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Board Context Toggle */}
                        <div className="ExportModal__section">
                            <label className="ExportModal__checkbox-item ExportModal__checkbox-item--toggle">
                                <input
                                    type="checkbox"
                                    checked={includeBoardContext}
                                    onChange={(e) => setIncludeBoardContext(e.target.checked)}
                                />
                                <span>Include board context</span>
                                <span className="ExportModal__hint">(name, description, columns)</span>
                            </label>
                        </div>

                        {/* Generate Button */}
                        <div className="ExportModal__actions">
                            {previewError && (
                                <div className="ExportModal__error">{previewError}</div>
                            )}
                            <button
                                className="ExportModal__generate-btn"
                                onClick={handleGeneratePreview}
                                disabled={!canGenerate || previewLoading}
                            >
                                {previewLoading ? (
                                    <><span className="btn-spinner" /> Generating...</>
                                ) : (
                                    <><i className="fas fa-magic" /> Generate Export</>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="ExportModal__body">
                        {/* Preview */}
                        <div className="ExportModal__preview-header">
                            <div className="ExportModal__preview-stats">
                                <span><i className="fas fa-tasks" /> {taskCount} tasks</span>
                                <span><i className="fas fa-text-width" /> {charCount.toLocaleString()} chars</span>
                                <span className="ExportModal__token-estimate">
                                    ~{Math.ceil(charCount / 4).toLocaleString()} tokens
                                </span>
                            </div>
                        </div>

                        <pre className="ExportModal__preview">{preview}</pre>

                        <div className="ExportModal__export-actions">
                            <button
                                className="ExportModal__copy-btn"
                                onClick={handleCopy}
                            >
                                <i className={copied ? 'fas fa-check' : 'fas fa-copy'} />
                                {copied ? 'Copied!' : 'Copy to Clipboard'}
                            </button>
                            <button
                                className="ExportModal__download-btn"
                                onClick={handleDownload}
                            >
                                <i className="fas fa-download" /> Download .{exportFormat === 'json' ? 'json' : 'md'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExportModal;
